import { v4 } from 'uuid';
import { ObservableItemIdentifier } from '../api-contracts/other/ObservableItemIdentifier';
import { InspectDatabase, inspectionDB } from './database.structure';
import { ObservableLevelsEnum } from './enums';
import {
  AssetModel,
  EquipmentModel,
  EquipmentPartModel,
  MineLocationModel,
  ObservationModel,
  PartDimensionModel,
  PartModel,
} from './types';
import DateHelpers from '../helpers/dateHelpers';

/** Shared across all DatabaseAssetsMethods instances (they are constructed ad hoc per
 * call site) - see getAssetById's in-flight dedupe. */
const inFlightAssetJoins = new Map<string, Promise<unknown>>();

/** anyOf REJECTS on invalid IndexedDB keys (undefined/null) - malformed rows must stay
 * silently skipped (as whole-store joins always did), never fatal. */
const validKeys = <T>(keys: (T | null | undefined)[]): T[] =>
  keys.filter((k): k is T => k !== null && k !== undefined);

interface InspectionAssetEquipment {
  bucket?: EquipmentModel;
  unassignedEquipment: EquipmentModel[];
  isChanged: boolean;
}

/**
 * A change-out is only real when the inspector DECLARED one - a positive
 * TotalParts/TotalPins/TotalBases count at the position. Specification
 * observations (SameParts/NewParts/PartId) alone are not a change-out: the
 * spec control seeds defaults when a position screen is opened, and cloning
 * on spec presence created a brand-new server part row (install date = today)
 * for every position that merely received a measurement, fragmenting part-age
 * history (found in the RT-B round trip: 4 phantom part rows). Mirrors the
 * declared-change guards in buildChangeOutSyncItems and the server distiller.
 */
export const hasDeclaredChangeAt = (
  observations: ObservationModel[],
  entityExternalId: string,
): boolean => {
  const countPaths = [
    ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools.ChangeOut
      .Position.TotalParts,
    ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools.ChangeOut
      .Position.TotalPins,
    ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools.ChangeOut
      .Position.TotalBases,
  ];
  return observations.some(
    (observation) =>
      observation.entityExternalId === entityExternalId &&
      observation.dataLevelId === ObservableLevelsEnum.equipmentPart &&
      countPaths.includes(observation.observableItem.identifier) &&
      parseInt(observation.value, 10) > 0,
  );
};

export default class DatabaseAssetsMethods {
  private db: InspectDatabase;

  constructor(db: InspectDatabase) {
    this.db = db;
  }

  /**
   * Creates a new bucket from the equipment and observations.
   * It processes the observations to find changeout parts and updates the bucket's equipment parts accordingly.
   * It also removes the changeout observations from the observations array and updates the entityExternalId
   * of the remaining observations to a new UUID.
   * @param {Object} params - The parameters for the method.
   * @param {EquipmentModel} params.bucket - The bucket equipment model to be created.
   * @param {ObservationModel[]} params.observations - The observations related to the bucket.
   * @param {Date} params.effectiveDate - The effective date for the bucket and its parts.
   * @returns {Promise<{ bucket: EquipmentModel, observations: ObservationModel[] }>} - A promise that resolves to an object containing the updated bucket and observations.
   */
  public async createNewBucketFromEquipmentAndObservations({
    bucket,
    observations,
    effectiveDate,
  }: {
    bucket: EquipmentModel;
    observations: ObservationModel[];
    effectiveDate: Date;
  }): Promise<{ bucket: EquipmentModel; observations: ObservationModel[] }> {
    const bucketCopy = { ...bucket } as EquipmentModel;
    const observationsCopy = [...observations] as ObservationModel[];

    const equipmentPartChangeoutObservations = observations.filter(
      (observation) =>
        observation.dataLevelId === ObservableLevelsEnum.equipmentPart &&
        observation.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
            .ChangeOut.Specification.PartId,
    );

    const changeoutParts = await inspectionDB.parts
      .where('externalId')
      .anyOf(
        equipmentPartChangeoutObservations.map(
          (observation) => observation.value,
        ),
      )
      .toArray();
    const changeoutPartsPartDimensions = await inspectionDB.partDimensions
      .where('partId')
      .anyOf(changeoutParts.map((part) => part.id))
      .toArray();

    for (let i = 0; i < bucketCopy.equipmentParts.length; i++) {
      const bucketEquipmentPart = bucketCopy.equipmentParts[i];
      // Check if the part is in the changeout parts
      const changeoutPartIdObservation =
        equipmentPartChangeoutObservations.find(
          (observation) =>
            observation.entityExternalId === bucketEquipmentPart.externalId,
        );
      if (
        changeoutPartIdObservation &&
        hasDeclaredChangeAt(observations, bucketEquipmentPart.externalId)
      ) {
        const changeoutPart = changeoutParts.find(
          (part) => part.externalId === changeoutPartIdObservation.value,
        );
        if (changeoutPart) {
          // Remove all changeout observations for this part

          const changeoutObservations = observationsCopy.filter(
            (observation) =>
              observation.entityExternalId === bucketEquipmentPart.externalId &&
              observation.dataLevelId === ObservableLevelsEnum.equipmentPart &&
              (observation.observableItem.identifier ===
                ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
                  .ChangeOut.Specification.PartId ||
                observation.observableItem.identifier ===
                  ObservableItemIdentifier.Identifiers.Bucket
                    .GroundEngagingTools.ChangeOut.Specification.NewParts
                    .Checked ||
                observation.observableItem.identifier ===
                  ObservableItemIdentifier.Identifiers.Bucket
                    .GroundEngagingTools.ChangeOut.Specification.SameParts
                    .Checked),
          );
          // Remove exactly the matched changeout-spec observations. The previous
          // splice(indexOf(first), length) removed N CONSECUTIVE items starting at the
          // first match - the spec observations are not guaranteed adjacent, so it could
          // drop unrelated observations from the payload and leave spec rows behind
          // (silent payload corruption found during Claude-driven E2E).
          for (const changeoutObservation of changeoutObservations) {
            const index = observationsCopy.indexOf(changeoutObservation);
            if (index !== -1) {
              observationsCopy.splice(index, 1);
            }
          }

          const externalId = v4();

          // Replace all Observations with bucketEquipmentPart.externalId as the entityExternalId with the new externalId
          for (const observation of observationsCopy) {
            if (
              observation.entityExternalId === bucketEquipmentPart.externalId
            ) {
              observation.entityExternalId = externalId;
            }
          }

          // Get the part dimensions for the changeout part
          const partDimensions = changeoutPartsPartDimensions.filter(
            (dimension) => dimension.partId === changeoutPart.id,
          );
          changeoutPart.partDiementions = partDimensions;
          bucketCopy.equipmentParts[i] = {
            ...bucketEquipmentPart,
            partId: changeoutPart.id,
            part: changeoutPart,
            id: 0,
            externalId: externalId,
            effectiveDate: effectiveDate,
          };
          bucketCopy.equipmentParts[i].part = changeoutPart;
        }
      }
    }

    return { bucket: bucketCopy, observations: observationsCopy };
  }

  public async joinBucketEquipmentPartsObservationData({
    bucket,
    observations,
    effectiveDate,
  }: {
    bucket: EquipmentModel;
    observations: ObservationModel[];
    effectiveDate: Date;
  }): Promise<EquipmentModel> {
    const bucketCopy = { ...bucket } as EquipmentModel;

    const equipmentPartChangeoutObservations = observations.filter(
      (observation) =>
        observation.dataLevelId === ObservableLevelsEnum.equipmentPart &&
        observation.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.GroundEngagingTools
            .ChangeOut.Specification.PartId,
    );

    const changeoutParts = await inspectionDB.parts
      .where('externalId')
      .anyOf(
        equipmentPartChangeoutObservations.map(
          (observation) => observation.value,
        ),
      )
      .toArray();
    const changeoutPartsPartDimensions = await inspectionDB.partDimensions
      .where('partId')
      .anyOf(changeoutParts.map((part) => part.id))
      .toArray();

    for (let i = 0; i < bucketCopy.equipmentParts.length; i++) {
      const bucketEquipmentPart = bucketCopy.equipmentParts[i];
      // Check if the part is in the changeout parts
      const changeoutPartIdObservation =
        equipmentPartChangeoutObservations.find(
          (observation) =>
            observation.entityExternalId === bucketEquipmentPart.externalId,
        );
      if (
        changeoutPartIdObservation &&
        hasDeclaredChangeAt(observations, bucketEquipmentPart.externalId)
      ) {
        const changeoutPart = changeoutParts.find(
          (part) => part.externalId === changeoutPartIdObservation.value,
        );
        if (changeoutPart) {
          // Get the part dimensions for the changeout part
          const partDimensions = changeoutPartsPartDimensions.filter(
            (dimension) => dimension.partId === changeoutPart.id,
          );
          changeoutPart.partDiementions = partDimensions;
          bucketCopy.equipmentParts[i] = {
            ...bucketEquipmentPart,
            partId: changeoutPart.id,
            part: changeoutPart,
            id: 0,
            externalId: v4(),
            effectiveDate: effectiveDate,
          };
          bucketCopy.equipmentParts[i].part = changeoutPart;
        }
      }
    }

    return bucketCopy;
  }

  public async joinBucketObservationData({
    observations,
    bucket,
  }: {
    observations: ObservationModel[];
    bucket: EquipmentModel;
  }): Promise<EquipmentModel> {
    const bucketCopy = {
      ...bucket,
      id: bucket.id === 0 ? -1 : bucket.id,
    } as EquipmentModel;
    const bucketObservations = observations.filter(
      (observation) =>
        observation.entityExternalId == bucket.externalId &&
        observation.dataLevelId === ObservableLevelsEnum.equipment &&
        (observation.observableItem.identifier.startsWith(
          'Bucket.Specifications.',
        ) ||
          observation.observableItem.identifier.startsWith(
            'Bucket.Installation.',
          )),
    );

    bucketCopy.name =
      bucketObservations.find(
        (observation) =>
          observation.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.Specifications
            .CustomerBucketName,
      )?.value ?? bucketCopy.name;
    bucketCopy.capitalIndexNumber =
      bucketObservations.find(
        (observation) =>
          observation.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.Specifications
            .CapitalIndexNumber,
      )?.value ?? bucketCopy.capitalIndexNumber;
    bucketCopy.partNumber =
      bucketObservations.find(
        (observation) =>
          observation.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.Specifications.PartNumber,
      )?.value ?? bucketCopy.partNumber;
    bucketCopy.serialNumber =
      bucketObservations.find(
        (observation) =>
          observation.observableItem.identifier ===
          ObservableItemIdentifier.Identifiers.Bucket.Specifications
            .SerialNumber,
      )?.value ?? bucketCopy.serialNumber;
    bucketCopy.installDate = bucketObservations.find(
      (observation) =>
        observation.observableItem.identifier ===
        ObservableItemIdentifier.Identifiers.Bucket.Installation.InstallDate,
    )?.value
      ? new Date(
          bucketObservations.find(
            (observation) =>
              observation.observableItem.identifier ===
              ObservableItemIdentifier.Identifiers.Bucket.Installation
                .InstallDate,
          )!.value,
        )
      : bucketCopy.installDate;
    const manufacturerObservation = bucketObservations.find(
      (observation) =>
        observation.observableItem.identifier ===
        ObservableItemIdentifier.Identifiers.Bucket.Specifications.Manufacturer,
    )?.value;
    if (manufacturerObservation) {
      const manufacturer = await this.db.assetManufacturers
        .where('name')
        .equalsIgnoreCase(manufacturerObservation)
        .first();
      if (manufacturer) {
        bucketCopy.manufacturerId = manufacturer.id;
      }
    }

    bucketCopy.equipmentType = bucketCopy.equipmentType ?? 'Bucket';

    return bucketCopy;
  }

  async joinBucketEquipmentPartData({
    bucket,
    effectiveDate,
  }: {
    bucket: EquipmentModel;
    effectiveDate?: Date;
  }): Promise<EquipmentPartModel[]> {
    const equipmentParts = await this.db.equipmentParts
      .where('externalEquipmentId')
      .equals(bucket.externalId)
      .toArray();

    for (const equipmentPart of equipmentParts) {
      const part = await this.db.parts.get({ id: equipmentPart.partId });

      if (part) {
        equipmentPart.part = part;
      }
    }

    return effectiveDate
      ? this.processWithEffectiveDate(
          equipmentParts.filter((ep) => ep.part !== undefined),
          effectiveDate,
        )
      : equipmentParts;
  }

  deduplicateEquipmentParts(
    equipmentParts: EquipmentPartModel[],
  ): EquipmentPartModel[] {
    const map = new Map<string, EquipmentPartModel>();

    for (const part of equipmentParts) {
      const key = [
        part.part.name,
        part.part.partTypeId,
        part.part.partCategoryId,
        part.position,
        part.locationId,
        part.secondaryLocationId,
        part.group,
      ].join('|');

      const existing = map.get(key);

      if (
        !existing ||
        new Date(part.effectiveDate) > new Date(existing.effectiveDate)
      ) {
        map.set(key, part);
      }
    }

    return Array.from(map.values());
  }

  processWithEffectiveDate<T extends { effectiveDate: Date }>(
    items: T[],
    effectiveDate: Date,
  ): T[] {
    const filtered = items.filter((item) => {
      const itemEffectiveDate = new Date(
        item.effectiveDate.getFullYear(),
        item.effectiveDate.getMonth(),
        item.effectiveDate.getDate(),
      );
      return itemEffectiveDate <= effectiveDate;
    });

    if (filtered.every((item) => this.isEquipmentPartModel(item))) {
      // If all items are EquipmentPartModel, sort by effectiveDate descending
      return this.deduplicateEquipmentParts(
        filtered as EquipmentPartModel[],
      ) as unknown as T[];
    }

    return filtered;
  }

  isEquipmentPartModel(item: any): item is EquipmentPartModel {
    return (
      item &&
      typeof item.locationId === 'number' &&
      typeof item.group === 'number' &&
      typeof item.partId === 'number' &&
      item.effectiveDate instanceof Date
    );
  }

  public async getAssets({
    effectiveDate,
    includeEquipmentParts,
  }: {
    effectiveDate?: Date;
    includeEquipmentParts?: boolean;
  }): Promise<AssetModel[]> {
    const assets = await this.db.assets.toArray();

    if (assets.length === 0) {
      return [];
    }

    const assetStructured = await this.joinAssetData({
      assets,
      effectiveDate,
      includeEquipmentParts,
    });
    return assetStructured;
  }

  public async getAssetByInspectionId(
    inspectionExternalId: string,
  ): Promise<AssetModel | null> {
    const inspection = await this.db.inspections.get({
      externalId: inspectionExternalId,
    });

    if (!inspection) {
      return null;
    }

    return (
      (await this.getAssetById({
        assetId: inspection.assetId,
        effectiveDate: inspection.inspectionDate,
      })) ?? null
    );
  }

  public async getAssetById({
    assetId,
    effectiveDate,
    includeEquipmentParts,
  }: {
    assetId: number;
    effectiveDate?: Date;
    includeEquipmentParts?: boolean;
  }): Promise<AssetModel | undefined> {
    // In-flight dedupe: one navigation asks for the same asset join up to 3x
    // concurrently (inspection query + cached-equipment hook + live-observations
    // hook). Share a single join per (asset, effectiveDate, parts) key while it is
    // running - in-flight ONLY (cleared on settle), so sequential reads stay fresh.
    // Module-level map because service instances are constructed ad hoc per call site.
    const key = `${assetId}|${effectiveDate ? effectiveDate.getTime() : 'now'}|${includeEquipmentParts !== false}`;
    const existing = inFlightAssetJoins.get(key);
    if (existing) {
      return existing as Promise<AssetModel | undefined>;
    }
    const promise = this.getAssetByIdUncached({
      assetId,
      effectiveDate,
      includeEquipmentParts,
    }).finally(() => {
      inFlightAssetJoins.delete(key);
    });
    inFlightAssetJoins.set(key, promise);
    return promise;
  }

  /**
   * Scoped multi-asset join for LIST screens: joins exactly the requested assets
   * (<=25 stays on joinAssetData's indexed scoped path) instead of the whole fleet -
   * the Home screen previously joined ~1,380 assets to decorate <=20 list rows.
   */
  public async getAssetsByIds({
    assetIds,
    effectiveDate,
    includeEquipmentParts,
  }: {
    assetIds: number[];
    effectiveDate?: Date;
    includeEquipmentParts?: boolean;
  }): Promise<AssetModel[]> {
    const unique = validKeys([...new Set(assetIds)]);
    if (unique.length === 0) {
      return [];
    }
    const assets = await this.db.assets.where('id').anyOf(unique).toArray();
    if (assets.length === 0) {
      return [];
    }
    return this.joinAssetData({ assets, effectiveDate, includeEquipmentParts });
  }

  private async getAssetByIdUncached({
    assetId,
    effectiveDate,
    includeEquipmentParts,
  }: {
    assetId: number;
    effectiveDate?: Date;
    includeEquipmentParts?: boolean;
  }): Promise<AssetModel | undefined> {
    const asset = await this.db.assets.get({ id: assetId });

    if (!asset || asset === undefined) {
      return undefined;
    }

    const assetStructured = await this.joinAssetData({
      assets: [asset],
      effectiveDate,
      includeEquipmentParts,
    });

    if (assetStructured.length === 0) {
      return undefined;
    }

    return assetStructured[0];
  }

  private async joinAssetData({
    assets,
    effectiveDate = new Date(),
    includeEquipmentParts = true,
  }: {
    assets: AssetModel[];
    effectiveDate?: Date;
    includeEquipmentParts?: boolean;
  }): Promise<AssetModel[]> {
    // --- Step 1. Load data in parallel.
    // PERF: this join used to materialise EVERY row of every store (64k equipmentParts,
    // 8k parts, 5.7k partDimensions) to join what is usually ONE asset - measured live
    // at ~193k rows deserialised per screen navigation (the join runs on the hot
    // inspection-query path, so every observation-change invalidation re-paid it).
    // The big stores are now scoped through their indexes to just the requested assets;
    // the whole-store path only remains for large fleet-wide joins, and the part stores
    // are skipped entirely when includeEquipmentParts is false. Join semantics are
    // unchanged: the group-key logic is keyed by externalEquipmentId, so loading all
    // rows for the asset's equipment externalIds preserves every group intact.
    const SCOPED_JOIN_MAX_ASSETS = 25;
    const scoped = assets.length <= SCOPED_JOIN_MAX_ASSETS;
    const assetIds = validKeys(assets.map((a) => a.id));

    const [
      customers,
      assetManufacturers,
      assetModels,
      assetTypes,
      sites,
      equipmentss,
      equipmentTypes,
      equipmentCategories,
      equipmentConfigurations,
      mineLocations,
      partCategories,
      partTypes,
    ] = await Promise.all([
      this.db.customers.toArray(),
      this.db.assetManufacturers.toArray(),
      this.db.assetModels.toArray(),
      this.db.assetTypes.toArray(),
      this.db.sites.toArray(),
      scoped
        ? this.db.equipments.where('assetId').anyOf(assetIds).toArray()
        : this.db.equipments.toArray(),
      this.db.equipmentTypes.toArray(),
      this.db.equipmentCategories.toArray(),
      this.db.equipmentConfigurations.toArray(),
      this.db.mineLocations.toArray(),
      this.db.partCategories.toArray(),
      this.db.partTypes.toArray(),
    ]);

    let equipmentPartss: EquipmentPartModel[] = [];
    let parts: PartModel[] = [];
    let partDimensionss: PartDimensionModel[] = [];
    if (includeEquipmentParts) {
      equipmentPartss = scoped
        ? await this.db.equipmentParts
            .where('externalEquipmentId')
            .anyOf(validKeys(equipmentss.map((e) => e.externalId)))
            .toArray()
        : await this.db.equipmentParts.toArray();
      const partIds = validKeys([
        ...new Set(equipmentPartss.map((p) => p.partId)),
      ]);
      [parts, partDimensionss] = await Promise.all([
        scoped
          ? this.db.parts.where('id').anyOf(partIds).toArray()
          : this.db.parts.toArray(),
        scoped
          ? this.db.partDimensions.where('partId').anyOf(partIds).toArray()
          : this.db.partDimensions.toArray(),
      ]);
    }

    // --- Step 2. Build Maps for O(1) lookups
    const manufacturerMap = new Map(assetManufacturers.map((m) => [m.id, m]));
    const modelMap = new Map(assetModels.map((m) => [m.id, m]));
    const assetTypeMap = new Map(assetTypes.map((t) => [t.id, t]));
    const siteMap = new Map(sites.map((s) => [s.id, s]));
    const customerMap = new Map(customers.map((c) => [c.id, c]));
    const equipmentByAsset = new Map<number, EquipmentModel[]>();
    for (const e of equipmentss) {
      if (!equipmentByAsset.has(e.assetId)) equipmentByAsset.set(e.assetId, []);
      equipmentByAsset.get(e.assetId)!.push(e);
    }

    const equipmentTypeMap = new Map(equipmentTypes.map((t) => [t.id, t]));
    const equipmentCategoryMap = new Map(
      equipmentCategories.map((c) => [c.id, c]),
    );
    const equipmentConfigMap = new Map(
      equipmentConfigurations.map((c) => [c.id, c]),
    );

    const mineLocationMap = new Map(mineLocations.map((l) => [l.id, l]));
    const partMap = new Map(parts.map((p) => [p.id, p]));
    const partDimensionsByPart = new Map<number, PartDimensionModel[]>();
    for (const d of partDimensionss) {
      if (!partDimensionsByPart.has(d.partId))
        partDimensionsByPart.set(d.partId, []);
      partDimensionsByPart.get(d.partId)!.push(d);
    }
    const partTypeMap = new Map(partTypes.map((t) => [t.id, t]));
    const partCategoryMap = new Map(partCategories.map((c) => [c.id, c]));

    // --- Step 3. Group EquipmentParts by composite key for fast lookups
    const makePartKey = (p: EquipmentPartModel) =>
      `${p.externalEquipmentId}|${p.partId}|${p.locationId}|${p.secondaryLocationId}|${p.position}`;

    const equipmentPartsGrouped = new Map<string, EquipmentPartModel[]>();
    for (const p of equipmentPartss) {
      const key = makePartKey(p);
      if (!equipmentPartsGrouped.has(key)) equipmentPartsGrouped.set(key, []);
      equipmentPartsGrouped.get(key)!.push(p);
    }

    // Pre-sort each group by effectiveDate descending (once)
    for (const parts of equipmentPartsGrouped.values()) {
      parts.sort(
        (a, b) => b.effectiveDate.getTime() - a.effectiveDate.getTime(),
      );
    }

    const currentDate = effectiveDate ?? new Date();
    const assetsStructured: AssetModel[] = [];

    // --- Step 4. Main join logic
    for (const asset of assets) {
      const assetStructured: AssetModel = { ...asset, equipments: [] };

      // Basic joins via Maps
      const manufacturer = manufacturerMap.get(asset.manufacturerId);
      const model = modelMap.get(asset.modelId);
      const assetType = assetTypeMap.get(asset.assetTypeId);
      const site = siteMap.get(asset.siteId);
      const customer = site ? customerMap.get(site.customerId) : undefined;

      if (manufacturer) assetStructured.manufacturerId = manufacturer.id;
      if (model) {
        model.assetType = assetType?.name ?? 'Unknown';
        assetStructured.model = model;
      }
      if (assetType) assetStructured.assetTypeId = assetType.id;
      if (site) {
        assetStructured.siteName = site.name;
        assetStructured.siteId = site.id;
      }
      if (customer) {
        assetStructured.customerId = customer.id;
        assetStructured.customerName = customer.name;
      }

      // --- Equipments
      const equipments = equipmentByAsset.get(asset.id) ?? [];
      for (const equipment of equipments) {
        const equipmentStructured: EquipmentModel = {
          ...equipment,
          equipmentParts: [],
        };

        const config = equipmentConfigMap.get(equipment.configurationId ?? -1);
        const type = equipmentTypeMap.get(equipment.equipmentTypeId);
        const category = type
          ? equipmentCategoryMap.get(type.equipmentCategoryId)
          : undefined;

        equipmentStructured.equipmentType = type?.name ?? 'Unknown';
        equipmentStructured.equipmentCategory = category?.name ?? 'Unknown';
        equipmentStructured.configurationId = config?.id ?? -1;

        if (!includeEquipmentParts) {
          equipmentStructured.equipmentParts = [];
          assetStructured.equipments.push(equipmentStructured);
          continue;
        }

        const filteredEquipmentParts = equipmentPartss.filter(
          (p) => p.externalEquipmentId === equipment.externalId,
        );

        // --- Equipment Parts
        for (const equipmentPart of filteredEquipmentParts) {
          const groupKey = makePartKey(equipmentPart);
          const relatedGroup = equipmentPartsGrouped.get(groupKey);
          if (!relatedGroup || relatedGroup.length === 0) continue;

          // Get latest valid equipment part before effectiveDate
          const latest = relatedGroup.find(
            (p) => p.effectiveDate <= currentDate,
          );
          if (!latest || latest.id !== equipmentPart.id) continue;

          const equipmentPartStructured = {
            ...equipmentPart,
          } as EquipmentPartModel;

          // Join mine location
          const location = mineLocationMap.get(equipmentPart.locationId);
          equipmentPartStructured.locationName = location?.name ?? 'Unknown';

          // Join Part and related info
          const part = partMap.get(equipmentPart.partId);
          if (!part) continue;

          const dimensions = partDimensionsByPart.get(part.id) ?? [];
          const partType = partTypeMap.get(part.partTypeId);
          const partCategory = partType
            ? partCategoryMap.get(partType.categoryId)
            : undefined;

          part.partDiementions = dimensions;
          part.partType = partType?.name ?? 'Unknown';
          part.partTypeId = partType?.id ?? -1;
          part.partCategory = partCategory?.name ?? 'Unknown';
          part.partCategoryId = partCategory?.id ?? -1;

          equipmentPartStructured.part = part;

          equipmentStructured.equipmentParts.push(equipmentPartStructured);
        }

        // Optional deduplication
        equipmentStructured.equipmentParts = this.deduplicateEquipmentParts(
          equipmentStructured.equipmentParts,
        );

        equipmentStructured.manufacturerId = manufacturer?.id ?? -1;
        assetStructured.equipments.push(equipmentStructured);
      }

      assetsStructured.push(assetStructured);
    }

    return assetsStructured;
  }

  public async getAssetEquipment(assetId: number): Promise<EquipmentModel[]> {
    const asset = await this.getAssetById({ assetId });

    if (!asset) {
      return [];
    }

    return asset.equipments;
  }

  async getAssetEquipmentPartById(
    equipmentPartId: number,
    categoryName: string,
    partName: string,
  ): Promise<EquipmentPartModel | null> {
    try {
      const equipmentPart = await this.db.equipmentParts
        .where('id')
        .equals(equipmentPartId)
        .first();

      if (!equipmentPart) {
        return null;
      }

      const part = await this.db.parts
        .where('id')
        .equals(equipmentPart.partId)
        .first();

      if (!part) {
        return null;
      }

      if (part.name !== partName) {
        return null;
      }

      const partType = await this.db.partTypes.get(part.partTypeId);

      if (!partType) {
        return null;
      }

      const partCategory = await this.db.partCategories.get(
        partType.categoryId,
      );

      if (!partCategory) {
        return null;
      }

      if (partCategory?.name == categoryName) {
        const equipmentPart = await this.db.equipmentParts
          .where('id')
          .equals(equipmentPartId)
          .first();

        if (equipmentPart) {
          // Attach the full part data
          equipmentPart.part = part;

          // Optionally load part dimensions if needed
          equipmentPart.part.partDiementions = await this.db.partDimensions
            .where('partId')
            .equals(part.id)
            .toArray();
        }

        return equipmentPart ?? null;
      }

      return null;
    } catch (error) {
      console.error('Error fetching asset equipment part:', error);
      throw error;
    }
  }

  async getAssetEquipmentPart(
    equipmentId: number,
    categoryName: string,
    partName: string,
  ): Promise<EquipmentPartModel | null> {
    try {
      const equipmentParts = await this.db.equipmentParts
        .where('equipmentId')
        .equals(equipmentId)
        .toArray();

      if (equipmentParts.length === 0) {
        return null;
      }

      const parts = await this.db.parts
        .where('id')
        .anyOf(equipmentParts.map((ep) => ep.partId))
        .toArray();

      if (parts.length === 0) {
        return null;
      }

      const matchingParts = parts.filter((part) => part.name === partName);

      if (matchingParts.length === 0) {
        return null;
      }

      for (const part of matchingParts) {
        const partType = await this.db.partTypes.get(part.partTypeId);

        if (!partType) {
          continue;
        }

        const partCategory = await this.db.partCategories.get(
          partType.categoryId,
        );

        if (!partCategory) {
          continue;
        }

        if (partCategory?.name == categoryName) {
          const equipmentPart = equipmentParts.find(
            (ep) => ep.partId === part.id,
          );

          if (equipmentPart) {
            // Attach the full part data
            equipmentPart.part = part;

            // Optionally load part dimensions if needed
            equipmentPart.part.partDiementions = await this.db.partDimensions
              .where('partId')
              .equals(part.id)
              .toArray();
          }

          return equipmentPart ?? null;
        }
      }

      return null;
    } catch (error) {
      console.error('Error fetching asset equipment part:', error);
      throw error;
    }
  }

  async getAssetEquipmentParts(
    equipmentId: string,
    categoryName: string,
    partName: string,
  ): Promise<EquipmentPartModel[]> {
    try {
      const equipmentParts = await this.db.equipmentParts
        .where('externalEquipmentId')
        .equals(equipmentId)
        .toArray();

      if (equipmentParts.length === 0) {
        return [];
      }

      const parts = await this.db.parts
        .where('id')
        .anyOf(equipmentParts.map((ep) => ep.partId))
        .toArray();

      if (parts.length === 0) {
        return [];
      }

      const matchingParts = parts.filter((part) => part.name === partName);

      if (matchingParts.length === 0) {
        return [];
      }

      for (const part of matchingParts) {
        const partType = await this.db.partTypes.get(part.partTypeId);

        if (!partType) {
          continue;
        }

        const partCategory = await this.db.partCategories.get(
          partType.categoryId,
        );

        if (!partCategory) {
          continue;
        }

        if (partCategory?.name == categoryName) {
          const equipmentPart = await this.db.equipmentParts
            .where(['externalEquipmentId', 'partId'])
            .equals([equipmentId, part.id])
            .toArray();

          for (const equipmentPart of equipmentParts) {
            // Attach the full part data
            equipmentPart.part = part;

            // Optionally load part dimensions if needed
            equipmentPart.part.partDiementions = await this.db.partDimensions
              .where('partId')
              .equals(part.id)
              .toArray();
          }

          return equipmentPart;
        }
      }

      return [];
    } catch (error) {
      console.error('Error fetching asset equipment part:', error);
      throw error;
    }
  }

  async getAssetUnassignedEquipment(
    assetId: number,
    effectiveDate?: Date,
  ): Promise<EquipmentModel[]> {
    const asset = await this.db.assets.get({ id: assetId });

    if (!asset) {
      return [];
    }

    const mineLocations = await this.db.mineLocations
      .where('siteId')
      .equals(asset.siteId)
      .toArray();

    if (mineLocations.length === 0) {
      return [];
    }

    const equipments = await this.db.equipments
      .where('mineLocationId')
      .anyOf(mineLocations.map((ml) => ml.id))
      .toArray();

    for (let i = 0; i < equipments.length; i++) {
      const equipment = equipments[i];
      const equipmentParts = await this.db.equipmentParts
        .where('externalEquipmentId')
        .equals(equipment.externalId)
        .toArray();

      for (const equipmentPart of equipmentParts) {
        const part = await this.db.parts.get({ id: equipmentPart.partId });

        if (part) {
          equipmentPart.part = part;
        }
      }

      // Join Equipment Type
      const equipmentType = await inspectionDB.equipmentTypes.get(
        equipment.equipmentTypeId,
      );
      if (equipmentType) {
        equipment.equipmentType = equipmentType.name;
      }

      equipment.equipmentParts = equipmentParts;
    }

    // const equipment = await this.db.equipments.where('mineLocationId').anyOf(mineLocations.map(id => id.id)).toArray();

    return effectiveDate
      ? this.processWithEffectiveDate(equipments, effectiveDate)
      : equipments;
  }

  async getInspectionAssetCurrentEquipment({
    inspectionId,
  }: {
    inspectionId: string;
  }): Promise<InspectionAssetEquipment> {
    const inspection = await this.db.inspections.get({
      externalId: inspectionId,
    });

    if (!inspection) {
      return { unassignedEquipment: [], isChanged: false };
    }

    let asset: AssetModel | undefined = undefined;
    let mineLocation: MineLocationModel | undefined = undefined;
    let mineLocationId: number = -1;

    // =======================
    // CASE 1: Has Asset
    // =======================
    if (inspection.assetId > 0) {
      asset = await this.getAssetById({
        assetId: inspection.assetId,
        effectiveDate: inspection.inspectionDate,
      });

      if (!asset) {
        return { unassignedEquipment: [], isChanged: false };
      }

      const site = await this.db.sites.get({ id: asset.siteId });
      if (site) {
        const mineLocations = await this.db.mineLocations
          .where('siteId')
          .equals(site.id)
          .toArray();
        if (mineLocations.length > 0) {
          mineLocation = mineLocations[0];
          mineLocationId = mineLocation.id;
        }
      }
    }
    // =======================
    // CASE 2: Has Mine Location
    // =======================
    else if (inspection.mineLocationId > 0) {
      mineLocation = await this.db.mineLocations.get({
        id: inspection.mineLocationId,
      });

      if (!mineLocation) {
        return { unassignedEquipment: [], isChanged: false };
      }

      // If bucket-only inspection
      if (inspection.equipmentExternalId) {
        let bucket = await this.db.equipments
          .where('externalId')
          .equals(inspection.equipmentExternalId)
          .first();

        if (bucket) {
          // Compare by calendar day: effectiveDate is noon UTC, inspectionDate is local.
          if (
            !DateHelpers.isEffectiveOnOrBeforeDay(
              bucket.effectiveDate,
              inspection.inspectionDate,
            )
          ) {
            return { unassignedEquipment: [], isChanged: false };
          }

          bucket = await this.joinBucketObservationData({
            observations:
              inspection.inspectionCategoryTypes[0]?.observations ?? [],
            bucket,
          });

          bucket.equipmentParts = await this.joinBucketEquipmentPartData({
            bucket,
            effectiveDate: inspection.inspectionDate,
          });
        }

        return { bucket, unassignedEquipment: [], isChanged: false };
      } else {
        const equipments = await this.db.equipments
          .where('mineLocationId')
          .equals(mineLocation.id)
          .toArray();

        return {
          unassignedEquipment: equipments.filter(
            (e) => e.id > 0 && e.effectiveDate <= inspection.inspectionDate,
          ),
          isChanged: false,
        };
      }
    }
    // =======================
    // CASE 3: No Asset / Location
    // =======================
    else {
      return { unassignedEquipment: [], isChanged: false };
    }

    // =======================
    // Asset Unassigned Equipment
    // =======================
    const unassignedEquipments = await this.getAssetUnassignedEquipment(
      asset.id,
      inspection.inspectionDate,
    );

    // =======================
    // Bucket-only for asset
    // =======================
    if (inspection.equipmentExternalId) {
      let bucket = await this.db.equipments
        .where('externalId')
        .equals(inspection.equipmentExternalId)
        .first();

      if (bucket) {
        // Compare by calendar day: effectiveDate is noon UTC, inspectionDate is local,
        // so a same-day install must not be treated as being in the future.
        if (
          !DateHelpers.isEffectiveOnOrBeforeDay(
            bucket.effectiveDate,
            inspection.inspectionDate,
          )
        ) {
          return { unassignedEquipment: [], isChanged: false };
        }

        bucket = await this.joinBucketObservationData({
          observations:
            inspection.inspectionCategoryTypes[0]?.observations ?? [],
          bucket,
        });

        const equipmentParts = await this.joinBucketEquipmentPartData({
          bucket,
          effectiveDate: inspection.inspectionDate,
        });
        bucket.equipmentParts = equipmentParts;
      }

      return { bucket, unassignedEquipment: [], isChanged: false };
    }

    // =======================
    // Changeout detection
    // =======================
    // A missing category (e.g. an inspection synced with no category types) leaves no
    // observations to inspect for change-out; fall through to the default bucket handling.
    const category = inspection.inspectionCategoryTypes[0];
    const obs = category?.observations ?? [];

    const changoutObservation = obs.find(
      (o) =>
        o.observableItem.identifier ===
        ObservableItemIdentifier.Identifiers.Bucket.IsChangeOut,
    );
    const bucketChangeoutFromObservation = obs.find(
      (o) =>
        o.observableItem.identifier ===
        ObservableItemIdentifier.Identifiers.Bucket.ChangeOutFrom,
    );
    const bucketChangeoutToObservation = obs.find(
      (o) =>
        o.observableItem.identifier ===
        ObservableItemIdentifier.Identifiers.Bucket.ExternalId,
    );

    if (changoutObservation && changoutObservation.value === 'true') {
      if (bucketChangeoutFromObservation && bucketChangeoutToObservation) {
        const bucketChangeoutFrom = asset.equipments.find(
          (e) =>
            e.equipmentType.toLowerCase() === 'bucket' &&
            e.externalId === bucketChangeoutFromObservation.value,
        );
        let bucketChangeoutTo = unassignedEquipments.find(
          (e) =>
            e.equipmentType.toLowerCase() === 'bucket' &&
            e.externalId === bucketChangeoutToObservation.value,
        );

        if (!bucketChangeoutTo) {
          bucketChangeoutTo = asset.equipments.find(
            (e) =>
              e.equipmentType.toLowerCase() === 'bucket' &&
              e.externalId === bucketChangeoutToObservation.value,
          );
        }

        if (bucketChangeoutFrom && bucketChangeoutTo) {
          // #10207: re-home the old bucket to the yard the incoming bucket
          // came from when known — not the site's arbitrary first mine
          // location, and never {assetId: -1, mineLocationId: -1} (which the
          // server rejects with "Must assign either assetId or mineLocationId").
          const incomingBucketYardId = bucketChangeoutTo.mineLocationId;
          bucketChangeoutFrom.assetId = -1;
          bucketChangeoutFrom.mineLocationId =
            incomingBucketYardId > 0 ? incomingBucketYardId : mineLocationId;
          bucketChangeoutTo.assetId = asset.id;
          bucketChangeoutTo.mineLocationId = -1;

          bucketChangeoutTo = await this.joinBucketObservationData({
            observations: obs,
            bucket: bucketChangeoutTo,
          });
          return {
            bucket: bucketChangeoutTo,
            unassignedEquipment: [
              ...unassignedEquipments.filter(
                (e) => e.externalId !== bucketChangeoutToObservation.value,
              ),
              bucketChangeoutFrom,
            ],
            isChanged: true,
          };
        }
      } else if (bucketChangeoutToObservation) {
        let bucketChangeoutTo = unassignedEquipments.find(
          (e) =>
            e.equipmentType.toLowerCase() === 'bucket' &&
            e.externalId === bucketChangeoutToObservation.value,
        );
        if (!bucketChangeoutTo) {
          bucketChangeoutTo = asset.equipments.find(
            (e) =>
              e.equipmentType.toLowerCase() === 'bucket' &&
              e.externalId === bucketChangeoutToObservation.value,
          );
        }

        if (bucketChangeoutTo) {
          bucketChangeoutTo = await this.joinBucketObservationData({
            observations: obs,
            bucket: bucketChangeoutTo,
          });

          bucketChangeoutTo.equipmentParts =
            await this.joinBucketEquipmentPartData({
              bucket: bucketChangeoutTo,
              effectiveDate: inspection.inspectionDate,
            });

          return {
            bucket: bucketChangeoutTo,
            unassignedEquipment: [
              ...unassignedEquipments.filter(
                (e) => e.externalId !== bucketChangeoutToObservation.value,
              ),
            ],
            isChanged: !!bucketChangeoutFromObservation,
          };
        }
      }
    }

    // =======================
    // Default: current bucket
    // =======================
    let bucket = asset.equipments.find(
      (e) => e.equipmentType.toLowerCase() === 'bucket' && e.id > 0,
    );

    if (bucket) {
      // Compare by calendar day: effectiveDate is noon UTC, inspectionDate is local,
      // so a same-day install must not be treated as being in the future.
      if (
        !DateHelpers.isEffectiveOnOrBeforeDay(
          bucket.effectiveDate,
          inspection.inspectionDate,
        )
      ) {
        return {
          unassignedEquipment: [
            ...unassignedEquipments.filter(
              (e) => e.effectiveDate <= inspection.inspectionDate,
            ),
          ],
          isChanged: false,
        };
      }
      bucket = await this.joinBucketObservationData({
        observations: obs,
        bucket,
      });

      bucket.equipmentParts = await this.joinBucketEquipmentPartData({
        bucket,
        effectiveDate: inspection.inspectionDate,
      });
    }

    return {
      bucket,
      unassignedEquipment: [...unassignedEquipments],
      isChanged: false,
    };
  }

  async addEqupmentToAsset(equipment: EquipmentModel) {
    if (equipment.assetId > 0) {
      const asset = await this.db.assets.get({ id: equipment.assetId });
      if (!asset) {
        throw new Error('Asset not found');
      }
    } else if (equipment.mineLocationId > 0) {
      const mineLocation = await this.db.mineLocations.get({
        id: equipment.mineLocationId,
      });
      if (!mineLocation) {
        throw new Error('Mine Location not found');
      }
    } else {
      throw new Error(
        'Equipment must have either an assetId or mineLocationId',
      );
    }

    await this.db.equipments.add(equipment);

    await this.db.equipmentParts.bulkPut(equipment.equipmentParts);
  }
}
